import React, { FC, useContext, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { User } from 'models/user';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';

const ResponsibleUserAutocomplete: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const { order, updateOrder } = useContext(SupplierOrderContext);
    const [labUsers, setLabUsers] = useState<User[]>([]);
    const { showBoundary } = useDenthubErrorBoundary();

    const { loading: isLabUserListLoading } = useQuery(getTechniciansForUserListTemplate, {
        variables: {
            filters: { active: true, role: 'technician' },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setLabUsers(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleResponsibleUserChange = (_event: React.SyntheticEvent, selectedUser: User | null) => {
        updateOrder({ dentist: selectedUser || undefined });
    };

    return (
        <Autocomplete
            options={labUsers}
            value={(order.dentist as User) || null}
            loading={isLabUserListLoading}
            fullWidth
            onChange={handleResponsibleUserChange}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            getOptionLabel={(option) => option.fullName || ''}
            renderInput={(inputParams) => (
                <TextField
                    {...inputParams}
                    label={translate('orders:RESPONSIBLE_USER')}
                    required
                    fullWidth
                    data-test="responsible-user-autocomplete.autocomplete"
                />
            )}
        />
    );
};

export default ResponsibleUserAutocomplete;
